import { QuoteProductItem } from '../../interfaces';
import { parseJSON } from './parseJSON';

import { PricingEndUserInfo, Bundle } from '../../interfaces/QuoteItem.types';
import { QuoteInformationAtom } from './atom';
import { sortQuoteLines } from './quoteLine.utils';

export const generateProductInformationPayload = (
  products?: QuoteProductItem[] | null,
  branchNumber?: string,
  siteCode?: string
) => {
  const sCode = siteCode?.toUpperCase();
  const productItems: any = [];

  if (!products) {
    return productItems;
  }

  products.forEach((product) => {
    if (product.skuVpnNumber) {
      productItems.push({
        branchNumber: branchNumber,
        quantity: Number(product.quantity),
        skuVpnNumber: product.skuVpnNumber.trim(),
      });
    }
    if (product.bundleSkus && product.bundleSkus.length > 0) {
      product.bundleSkus.forEach((sku: string) => {
        const bundleSkuQuantity = getSkuQuantity(sku, product.bundle);
        productItems.push({
          branchNumber: branchNumber,
          isKitComponent: true,
          quantity: bundleSkuQuantity,
          skuVpnNumber: sCode === 'BR' || sCode === 'IN' ? sku : sku?.slice(-7),
        });
      });
    }
  });
  return productItems;
};

export const generateProductLines = (
  products?: QuoteProductItem[],
  validSkuArray?: any[],
  siteCode?: string
) => {
  const sCode = siteCode?.toUpperCase();
  const quoteDetails: any = localStorage.getItem('entire_quote_values');
  const profitPriceData: any = JSON.parse(quoteDetails);
  const editQuotelineItems: any[] = [];

  if (!products) {
    return null;
  }

  products?.forEach((product) => {
    const filteredProducts = validSkuArray?.filter((validSku: any) => {
      const skuVPNNumber = product?.skuVpnNumber.toUpperCase().trim();
      return (
        skuVPNNumber === validSku?.productDetails[0]?.ingramPartNumber ||
        skuVPNNumber === validSku?.vendorPartNumber
      );
    });
    const bundleSkus: any[] = [];
    product?.bundleSkus?.forEach((bundleSKU: string) => {
      const sku =
        sCode === 'BR' || sCode === 'IN' ? bundleSKU : bundleSKU.slice(-7);
      validSkuArray?.forEach((validSku: any) => {
        const skuVPNNumber = sku.toUpperCase().trim();
        if (
          skuVPNNumber === validSku?.productDetails[0]?.ingramPartNumber ||
          skuVPNNumber === validSku?.vendorPartNumber
        ) {
          bundleSkus.push(validSku);
        }
      });
    });
    if (filteredProducts && filteredProducts.length > 0) {
      filteredProducts.forEach((element: any) => {
        const newLine: any = {
          discountResponse: null,
          duplicateselected: true,
          globalpartnumber: element?.productDetails[0]?.globalpartnumber,
          harmonizedCode: element?.productDetails[0]?.harmonizedCode,
          hasWarrantyProduct: product.hasWarrantyProduct,
          imageurl: element?.productDetails[0]?.productImage,
          isParentItem: bundleSkus?.length > 0,
          isWarranty: element?.productDetails[0]?.isWarranty,
          isWarrantyProduct: product.isWarrantyProduct,
          isenduserrequired:
            element?.productDetails[0]?.isenduserrequired ?? false,
          lineId: '',
          longdescription: element?.productDetails[0]?.longDescription,
          markUp: profitPriceData?.markupValue
            ? profitPriceData?.markupValue
            : '',
          markUpUnit: profitPriceData?.markupUnit
            ? profitPriceData?.markupUnit
            : '',
          parentProduct: product?.parentProduct,
          quantity: product?.quantity?.toString(),
          recommendationID: product?.recommendationRecordId?.includes('|')
            ? product?.recommendationRecordId?.split('|')?.[1]
            : product?.recommendationRecordId,
          shortDescription: element?.productDetails[0]?.shortDescription,
          shouldCreateNewLine: product.shouldCreateNewLine || false,
          skuvpn: element?.productDetails[0]?.ingramPartNumber,
          vendorNumber: element?.productDetails[0]?.vendorNumber,
          vpn: element?.productDetails[0]?.vendorPartNumber,
        };
        if (product.parentWarrantyLineNumber) {
          newLine.parentWarrantyLineNumber = product.parentWarrantyLineNumber;
        }
        if (product.linkWarrantyRefNumber) {
          newLine.linkWarrantyRefNumber = product.linkWarrantyRefNumber;
        }
        editQuotelineItems.push(newLine);
      });
      const bundleQuantity: any =
        localStorage.getItem('bundleQuantity') || null;
      const newBundleRes = JSON.parse(bundleQuantity);
      bundleSkus.forEach((element: any) => {
        const bundleSkuQuantity = getSkuQuantity(
          newBundleRes,
          element?.productDetails[0]?.ingramPartNumber
        );
        editQuotelineItems.push({
          discountResponse: null,
          duplicateselected: true,
          globalpartnumber: element?.productDetails[0]?.globalpartnumber,
          harmonizedCode: element?.productDetails[0]?.harmonizedCode,
          imageurl: element?.productDetails[0]?.productImage,
          isChildItem: true,
          lineId: '',
          longdescription: element?.productDetails[0]?.longDescription,
          markUp: profitPriceData?.markupValue
            ? profitPriceData?.markupValue
            : '',
          markUpUnit: profitPriceData?.markupUnit
            ? profitPriceData?.markupUnit
            : '',
          parentProduct: product?.skuVpnNumber,
          quantity: bundleSkuQuantity?.toString() || '1',
          recommendationID: product?.recommendationRecordId?.includes('|')
            ? product?.recommendationRecordId?.split('|')?.[1]
            : product?.recommendationRecordId,
          shortDescription: element?.productDetails[0]?.shortDescription,
          skuvpn: element?.productDetails[0]?.ingramPartNumber,
          vendorNumber: element?.productDetails[0]?.vendorNumber,
          vpn: element?.productDetails[0]?.vendorPartNumber,
        });
      });
    }
  });

  return editQuotelineItems;
};

export const generateQuoteLinesPayload = (
  quoteLines: any,
  requestType: string,
  quoteInfo: QuoteInformationAtom
) => {
  const { branchNumber, quoteGuid, quoteNumber } = quoteInfo;
  const pricingDetails: any = localStorage.getItem('priceFilterOpt');
  const endUserString = localStorage.getItem(`endUserData:${quoteNumber}`);
  const endUserData = parseJSON(endUserString, '');

  const type = endUserData?.IndustrySegmentation;
  const endUserInformation = getPricingEndUserInfo(quoteNumber);
  const attValue = endUserAttIndustrySegmentation(type);
  let parameter = '';

  if (!pricingDetails) {
    parameter = attValue;
  } else if (pricingDetails && pricingDetails !== 'myPrice') {
    parameter = pricingDetails;
  }

  const quoteLinePayload: any = {
    action: requestType,
    attributeName: 'governmentprogramcode',
    attributeValue: parameter,
    branchNumber: branchNumber,
    cmptoken: '',
    discount: '',
    endUserInformation: endUserInformation,
    entire_markUp: '',
    entire_markUpUnit: '',
    isChangeTax: true,
    isEUEnabled: '',
    lineProfitSeleced: '',
    profitValue: '',
    quoteguid: quoteGuid,
    quotenumber: quoteNumber,
    shipping: '',
    tax: '',
  };
  if (requestType === 'Updatequantity') {
    quoteLinePayload.QuoteLinesItems = quoteLines;
    quoteLinePayload.action = requestType;
  } else {
    quoteLinePayload.quotelinesitems = quoteLines;
  }
  return quoteLinePayload;
};

export const endUserAttIndustrySegmentation = (type?: string) => {
  let attValue = '';
  if (type === 'E') {
    attValue = 'PAE';
  } else if (type === 'F') {
    attValue = 'PAF';
  } else if (type === 'H') {
    attValue = 'EDH';
  } else if (type === 'K') {
    attValue = 'EDK';
  } else if (type === 'S') {
    attValue = 'PAS';
  }
  return attValue;
};

export const getPricingEndUserInfo = (
  quoteNumber: string
): PricingEndUserInfo | null => {
  if (!quoteNumber) return null;

  const endUserString = localStorage.getItem(`endUserData:${quoteNumber}`);
  const endUserLocalData: any = parseJSON(endUserString, '');

  if (!endUserLocalData) return null;

  const {
    AddressSequenceId,
    CmpAppUuid,
    ContactId,
    EndUserAcopId,
    IngramEndUserId,
  } = endUserLocalData;

  const endUserObj: PricingEndUserInfo = {
    CmpAppUuid: CmpAppUuid,
    endUserAcopId: EndUserAcopId,
    endUserAddressSeqId: AddressSequenceId,
    endUserContactId: ContactId,
    ingramEndUserId: IngramEndUserId,
  };

  return Object.values(endUserObj).some((value) => value) ? endUserObj : null;
};

export const getSkuQuantity = (sku: string, bundledSkus?: Bundle[]) => {
  let bundleSkuQuantity = 1;
  if (
    bundledSkus &&
    Array.isArray(bundledSkus) &&
    bundledSkus?.length > 0 &&
    sku
  ) {
    const currentBundleSku = bundledSkus?.find(
      (bSku: Bundle) => bSku.bundledSku === sku
    );
    bundleSkuQuantity = currentBundleSku?.quantity ?? 1;
  }
  return bundleSkuQuantity;
};

export function getRevisionNumber(quoteIdVal: string) {
  let revision;
  if (quoteIdVal !== null && quoteIdVal !== undefined) {
    const splitRevision = quoteIdVal.toString()?.split('_');
    if (splitRevision?.length > 1) {
      revision = splitRevision[1];
    } else {
      revision = '0';
    }
  }
  return revision;
}

export function matchDealWithExistingLine(
  quoteLines: any[],
  enableWarranty?: boolean,
  sku?: string,
  bidNumber?: string,
  basketLineId?: string
): number | null {
  if (!quoteLines || quoteLines?.length === 0) {
    return null;
  }

  const quoteLineIndex = sortQuoteLines(quoteLines, enableWarranty).findIndex(
    (quoteLine) =>
      quoteLine.sku === sku &&
      quoteLine.lineNumber !== basketLineId &&
      quoteLine?.vendorBid?.bidNumber &&
      quoteLine?.vendorBid?.bidNumber === bidNumber
  );

  if (quoteLineIndex !== -1) {
    return quoteLineIndex + 1;
  }
  return null;
}
